import i18n from '@/i18n';
import { imageSpaceAPI } from '@/utils/api';
import { getImageFromIndexedDB } from '@/utils/imageStorage';
import {
  normalizeGoodsImageRefToId,
  inlineUrlFromGoodsImageRef,
} from './goodsTemplateImageUrls';

const GOODS_PRINT_SESSION_KEY = 'ipe.goodsPrintUrlByImageId';
const GOODS_PRINT_SOURCE_SESSION_KEY = 'ipe.sourceCutoutTaskId';

/** crop_/cutout_/enhance_ 结果位图片空间 id（允许按 id 查图）；裸原图 id 不允许静默回退 */
export function isGoodsDerivedPrintSpaceId(imageId) {
  return /^(crop|cutout|enhance)_/i.test(String(imageId || '').trim());
}

const stampImageServerCache = new Map();

/**
 * 导出 / 入库解析印花结果 URL。
 * 优先级：goodsPrintUrl 映射 → 内联 url → 裁剪结果表 → 仅结果位 id 可查图片空间。
 * 默认禁止用裸原图 id 回退图片空间 / IndexedDB（避免「导出印花变原图」）。
 *
 * @param {{ id?: string, url?: string }} norm
 * @param {Record<string, string>} [cropImageUrlMap]
 * @param {object[]} [imageSpaceImages]
 * @param {Record<string, string>} [printUrlByImageId]
 * @param {{ allowOriginalImageSpaceFallback?: boolean }} [options]
 * @returns {Promise<string>}
 */
export async function resolveStampImageSourceForIngest(
  norm,
  cropImageUrlMap = {},
  imageSpaceImages = [],
  printUrlByImageId = {},
  options = {},
) {
  const allowOriginalImageSpaceFallback = Boolean(options?.allowOriginalImageSpaceFallback);
  const id = String(norm?.id || '').trim();

  if (id) {
    const fromPrintMap = resolveGoodsPrintUrl(id, printUrlByImageId);
    if (fromPrintMap) return fromPrintMap;
  }
  if (norm?.url && String(norm.url).trim()) return String(norm.url).trim();
  if (id && cropImageUrlMap?.[id]) return String(cropImageUrlMap[id]).trim();
  if (id && cropImageUrlMap?.[String(id)]) return String(cropImageUrlMap[String(id)]).trim();
  if (!id) return '';

  const allowSpaceLookup = allowOriginalImageSpaceFallback || isGoodsDerivedPrintSpaceId(id);
  if (!allowSpaceLookup) return '';

  const list = Array.isArray(imageSpaceImages) ? imageSpaceImages : [];
  const img = list.find((im) => String(im?.id) === id);
  const fromSpace = img?.dataUrl || img?.url || '';
  if (String(fromSpace).trim()) return String(fromSpace).trim();

  const cached = stampImageServerCache.get(id);
  if (cached && String(cached).trim()) return String(cached).trim();

  try {
    const res = await imageSpaceAPI.getById(id);
    const serverImage = res?.image || res?.data || res || {};
    const fromServer = serverImage?.dataUrl || serverImage?.url || '';
    if (String(fromServer).trim()) {
      stampImageServerCache.set(id, fromServer);
      return String(fromServer).trim();
    }
  } catch (_) {
    /* IndexedDB fallback below */
  }

  try {
    const fromDb = await getImageFromIndexedDB(id);
    if (fromDb && String(fromDb).trim()) return String(fromDb).trim();
  } catch (_) {
    /* ignore */
  }

  return '';
}

/**
 * 强制展开并固化印花 URL 映射（任务 result / session 共用）。
 * 有上游任务号时走 expand；否则仍展开 crop_/cutout_ 尾巴别名，避免只剩原图 id。
 */
export function buildPersistedGoodsPrintUrlMap(urlByImageId = {}, lineageTaskId = '') {
  const base = urlByImageId && typeof urlByImageId === 'object' && !Array.isArray(urlByImageId)
    ? urlByImageId
    : {};
  const lineage = String(lineageTaskId || '').trim();
  if (lineage) return expandGoodsPrintUrlByImageId(lineage, base);

  const out = {};
  Object.entries(base).forEach(([rawId, rawUrl]) => {
    const id = String(rawId || '').trim();
    const url = rawUrl != null ? String(rawUrl).trim() : '';
    if (!id || !url) return;
    out[id] = url;
    const cropTail = id.match(/^crop_[^_]+_(.+)$/i);
    if (cropTail?.[1]) out[cropTail[1]] = url;
    const cutoutTail = id.match(/^cutout_[^_]+_(.+)$/i);
    if (cutoutTail?.[1]) out[cutoutTail[1]] = url;
    const enhanceTail = id.match(/^enhance_[^_]+_(.+)$/i);
    if (enhanceTail?.[1]) out[enhanceTail[1]] = url;
  });
  return out;
}

export function formatMissingStampError(stampNames = []) {
  const names = (Array.isArray(stampNames) ? stampNames : [])
    .map((n) => String(n || '').trim())
    .filter(Boolean);
  const label = names.length > 0 ? names.join('、') : '未知印花位';
  return i18n.t('workbench:ipeUtilError.printMissingUrl', { label });
}

export function persistGoodsPrintUrlsToSession(sourceCutoutTaskId, urlByImageId) {
  try {
    sessionStorage.setItem(GOODS_PRINT_SESSION_KEY, JSON.stringify(urlByImageId || {}));
    if (sourceCutoutTaskId) {
      sessionStorage.setItem(GOODS_PRINT_SOURCE_SESSION_KEY, String(sourceCutoutTaskId));
    }
  } catch (_) {
    // ignore quota
  }
}

export function loadGoodsPrintUrlsFromSession() {
  try {
    const raw = sessionStorage.getItem(GOODS_PRINT_SESSION_KEY);
    if (!raw) return { urls: {}, sourceCutoutTaskId: '', sourceCropTaskId: '' };
    const urls = JSON.parse(raw);
    const sourceCutoutTaskId = sessionStorage.getItem(GOODS_PRINT_SOURCE_SESSION_KEY) || '';
    return {
      urls: urls && typeof urls === 'object' && !Array.isArray(urls) ? urls : {},
      sourceCutoutTaskId,
      /** 与 sourceCutoutTaskId 同值（历史 session 键名兼容） */
      sourceCropTaskId: sourceCutoutTaskId,
    };
  } catch (_) {
    return { urls: {}, sourceCutoutTaskId: '', sourceCropTaskId: '' };
  }
}

/**
 * 解析主图/POD 任务上游裁剪（或历史抠图）任务 ID。
 * 优先 sourceCropTaskId，兼容 sourceCutoutTaskId / 顶层 sourceTaskId。
 */
export function resolveGoodsSourceCropTaskId(taskOrResult) {
  if (!taskOrResult || typeof taskOrResult !== 'object') return '';
  const r = taskOrResult.result && typeof taskOrResult.result === 'object'
    ? taskOrResult.result
    : taskOrResult;
  const raw =
    r.sourceCropTaskId
    || r.sourceCutoutTaskId
    || taskOrResult.sourceCropTaskId
    || taskOrResult.sourceCutoutTaskId
    || taskOrResult.sourceTaskId
    || taskOrResult.source_task_id
    || '';
  return String(raw || '').trim();
}

/** 写入 result 的血缘字段（双写，兼容旧读路径） */
export function buildGoodsLineageFields(fromCropTaskId = '') {
  const id = String(fromCropTaskId || '').trim();
  if (!id) return {};
  return {
    sourceCropTaskId: id,
    sourceCutoutTaskId: id,
  };
}

export function clearGoodsPrintUrlsSession() {
  try {
    sessionStorage.removeItem(GOODS_PRINT_SESSION_KEY);
    sessionStorage.removeItem(GOODS_PRINT_SOURCE_SESSION_KEY);
  } catch (_) {
    // ignore
  }
}

/** crop_CROP-20260531-4643_LIB-xxx → 裁剪任务号 */
export function parseCropTaskIdFromCropSpaceId(spaceId) {
  const m = String(spaceId || '').match(/^crop_(CROP-\d{8}-\d+)_/i);
  return m ? m[1] : '';
}

/** cutout_{taskId}_{imageId} → 抠图任务号 */
export function parseCutoutTaskIdFromSpaceId(spaceId) {
  const m = String(spaceId || '').match(/^cutout_([^_]+)_/i);
  return m ? m[1] : '';
}

/** 从 crop_* 印花 id 推断上游裁剪任务号 */
export function inferCropTaskIdFromImageIds(imageIds = []) {
  for (const id of imageIds || []) {
    const cropId = parseCropTaskIdFromCropSpaceId(id);
    if (cropId) return cropId;
  }
  return '';
}

/** 从 cutout_* 印花 id 推断上游抠图任务号 */
export function inferCutoutTaskIdFromImageIds(imageIds = []) {
  for (const id of imageIds || []) {
    const cutoutId = parseCutoutTaskIdFromSpaceId(id);
    if (cutoutId) return cutoutId;
  }
  return '';
}

/** crop_CROP-20260531-4643_LIB-xxx → 原图 imageId */
export function parseOriginalImageIdFromCropSpaceId(spaceId) {
  const m = String(spaceId || '').match(/^crop_CROP-\d{8}-\d+_(.+)$/i);
  return m ? m[1] : String(spaceId || '');
}

export { resolveImageSpaceDisplayRawUrl as pickImageSpaceDisplayUrl } from './imageSpaceDisplayUrl';

/** 抠图详情进主图创建：优先使用图片空间里已保存的 cutout_ 条目 id */
export function goodsImageIdForDetailNextStep(module, taskId, imageId) {
  const sid = String(imageId ?? '').trim();
  if (!sid) return sid;
  if (module === 'cutout' && taskId) return `cutout_${taskId}_${sid}`;
  if (module === 'crop' && taskId) return `crop_${taskId}_${sid}`;
  return sid;
}

/** 为主图创建写入多种 imageId 别名，避免 imageGroups 与详情页 id 不一致 */
export function expandGoodsPrintUrlByImageId(taskId, urlByImageId) {
  const out = { ...(urlByImageId || {}) };
  Object.entries(out).forEach(([id, url]) => {
    const u = url && String(url).trim();
    if (!id || !u) return;
    const sid = String(id);
    out[sid] = u;
    if (taskId) {
      out[`cutout_${taskId}_${sid}`] = u;
      if (!sid.startsWith('crop_')) out[`crop_${taskId}_${sid}`] = u;
    }
    const cropTail = sid.match(/^crop_[^_]+_(.+)$/i);
    if (cropTail?.[1]) out[cropTail[1]] = u;
    const cutoutTail = sid.match(/^cutout_[^_]+_(.+)$/i);
    if (cutoutTail?.[1]) out[cutoutTail[1]] = u;
    const enhanceTail = sid.match(/^enhance_[^_]+_(.+)$/i);
    if (enhanceTail?.[1]) out[enhanceTail[1]] = u;
  });
  return out;
}

/** 按 id / cutout_ 前缀 / crop_ 后缀 解析印花结果 URL */
export function resolveGoodsPrintUrl(imageId, printUrlByImageId = {}) {
  if (!printUrlByImageId || typeof printUrlByImageId !== 'object') return '';
  const id = String(imageId ?? '').trim();
  if (!id) return '';
  const direct = printUrlByImageId[id] ?? printUrlByImageId[String(id)];
  if (direct && String(direct).trim()) return String(direct).trim();
  for (const [key, val] of Object.entries(printUrlByImageId)) {
    if (!val || !String(val).trim()) continue;
    if (key === id || key.endsWith(`_${id}`) || id.endsWith(`_${key}`)) {
      return String(val).trim();
    }
  }
  return '';
}

/** 将印花结果 URL 写入每个 imageGroup 条目（持久化到任务 result，详情页可直接用）
 * 方案三：写入 role=print + assetId，拒绝把 original 写进印花位。
 */
export function applyGoodsPrintUrlsToForms(goodsForms, printUrlByImageId) {
  const map = printUrlByImageId && typeof printUrlByImageId === 'object' ? printUrlByImageId : {};
  if (!Array.isArray(goodsForms) || Object.keys(map).length === 0) return goodsForms;

  return goodsForms.map((form) => ({
    ...form,
    imageGroups: (form.imageGroups || []).map((group) => {
      const next = {};
      Object.entries(group || {}).forEach(([stampName, ref]) => {
        const id = normalizeGoodsImageRefToId(ref);
        const resultUrl = resolveGoodsPrintUrl(id, map) || inlineUrlFromGoodsImageRef(ref);
        if (id && resultUrl) {
          const incomingRole = ref && typeof ref === 'object' ? String(ref.role || '').trim() : '';
          if (incomingRole && incomingRole !== 'print') {
            // 原图等非 print 角色不得进印花位：降级为仅保留 print URL 引用
            next[stampName] = {
              id: String(id),
              url: resultUrl,
              role: 'print',
              assetId: ref?.assetId && incomingRole === 'print' ? ref.assetId : undefined,
            };
          } else {
            next[stampName] = {
              id: String(id),
              url: resultUrl,
              role: 'print',
              assetId: ref && typeof ref === 'object' ? ref.assetId : undefined,
              name: ref && typeof ref === 'object' ? ref.name : undefined,
            };
          }
        } else {
          next[stampName] = ref;
        }
      });
      return next;
    }),
  }));
}

/** 从 goodsForms 内嵌 { id, url } 收集印花 URL（不覆盖已有键） */
export function collectGoodsPrintUrlsFromForms(goodsForms, baseMap = {}) {
  const out = { ...(baseMap || {}) };
  (goodsForms || []).forEach((form) => {
    (form.imageGroups || []).forEach((group) => {
      Object.values(group || {}).forEach((ref) => {
        const id = normalizeGoodsImageRefToId(ref);
        const url = inlineUrlFromGoodsImageRef(ref);
        if (!id || !url) return;
        const key = String(id);
        if (out[key] || out[id]) return;
        out[key] = url;
      });
    });
  });
  return out;
}
